/**
 * Partner request form component.
 *
 * Form for creating and editing partner requests. Handles form validation,
 * submission, and navigation. Supports both create and edit modes with
 * pre-populated data when editing.
 *
 * @module RequestForm
 */

import { useNavigate } from "react-router-dom";
import { usePartnerFinderPath } from "@/hooks/usePartnerFinderPath";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";
import { PartnerRequest, GermanLevel } from "@/types/partner-finder";
import { learningGoals, locations, DAYS } from "@/data/partnerFinderData";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useAuth } from "@/hooks/useAuth";
import { usePartnerRequestForm } from "@/hooks/usePartnerRequestForm";
import { useTranslation } from "react-i18next";
import {
  translatePartnerDay,
  translatePartnerGoal,
  translatePartnerLocation,
} from "@/lib/partnerFinderLabels";
import type { TFunction } from "i18next";

const LEVELS: GermanLevel[] = ["A1", "A2", "B1", "B2", "C1", "C2"];

const createFormSchema = (t: TFunction) =>
  z
    .object({
      level: z.enum(["A1", "A2", "B1", "B2", "C1", "C2"], {
        required_error: t("partnerFinder.form.validation.levelRequired", {
          defaultValue: "Please select your German level",
        }),
      }),
      meetingPreference: z.enum(["online", "in-person"], {
        required_error: t(
          "partnerFinder.form.validation.meetingPreferenceRequired",
          {
            defaultValue: "Please select a meeting preference",
          },
        ),
      }),
      goal: z.string().min(
        1,
        t("partnerFinder.form.validation.goalRequired", {
          defaultValue: "Please select a learning goal",
        }),
      ),
      availability: z
        .array(
          z.enum([
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
          ]),
        )
        .min(
          1,
          t("partnerFinder.form.validation.dayRequired", {
            defaultValue: "Please select at least one day",
          }),
        ),
      location: z.string().optional(),
      timezone: z.string().optional(),
      additionalInfo: z.string().min(
        10,
        t("partnerFinder.form.validation.additionalInfoMin", {
          defaultValue:
            "Please provide at least 10 characters of additional information",
        }),
      ),
    })
    .refine(
      (data) => {
        if (data.meetingPreference === "in-person") {
          return !!data.location;
        }
        return true;
      },
      {
        message: t("partnerFinder.form.validation.locationRequired", {
          defaultValue: "Location is required for in-person meetings",
        }),
        path: ["location"],
      },
    );

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

interface RequestFormProps {
  readonly onSubmit: (
    data: Omit<
      PartnerRequest,
      "id" | "userId" | "createdAt" | "updatedAt" | "status"
    >,
  ) => void;
  readonly initialData?: PartnerRequest;
}

export default function RequestForm({
  onSubmit,
  initialData,
}: RequestFormProps) {
  const { t } = useTranslation();
  const formSchema = createFormSchema(t);
  const navigate = useNavigate();
  const paths = usePartnerFinderPath();
  const { user } = useAuth();
  const { isSubmitting, handleSubmit: handleFormSubmit } =
    usePartnerRequestForm();
  const isEditMode = !!initialData;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      level: initialData?.level || "B1",
      meetingPreference: initialData?.meetingPreference || "online",
      goal: initialData?.goal || "",
      availability: initialData?.availability || [],
      location: initialData?.location || "",
      additionalInfo: initialData?.additionalInfo || "",
    },
  });

  const meetingPreference = form.watch("meetingPreference");

  const getSubmitButtonText = () => {
    if (isSubmitting)
      return t("partnerFinder.form.saving", { defaultValue: "Saving..." });
    if (isEditMode)
      return t("partnerFinder.form.saveChanges", {
        defaultValue: "Save Changes",
      });
    return t("partnerFinder.form.publishRequest", {
      defaultValue: "Publish Request",
    });
  };

  const createDayChangeHandler = (
    field: { value: string[]; onChange: (value: string[]) => void },
    day: string,
  ) => {
    const addDay = () => {
      field.onChange([...field.value, day]);
    };
    const removeDay = () => {
      field.onChange(field.value?.filter((value) => value !== day));
    };

    return (checked: boolean) => {
      const isCurrentlyIncluded = field.value?.includes(day);
      if (checked && !isCurrentlyIncluded) {
        addDay();
      } else if (!checked && isCurrentlyIncluded) {
        removeDay();
      }
    };
  };

  const handleSubmit = async (values: FormValues) => {
    if (!user?.id) {
      return;
    }

    // Ensure all required fields are present (they should be after Zod validation)
    if (
      !values.level ||
      !values.meetingPreference ||
      !values.goal ||
      !values.availability ||
      !values.additionalInfo
    ) {
      return;
    }

    const success = await handleFormSubmit(
      {
        level: values.level,
        meetingPreference: values.meetingPreference,
        goal: values.goal,
        availability: values.availability,
        location: values.location,
        timezone: values.timezone,
        additionalInfo: values.additionalInfo,
      },
      user.id,
      initialData,
    );
    if (success) {
      onSubmit({
        level: values.level,
        meetingPreference: values.meetingPreference,
        goal: values.goal,
        availability: values.availability,
        location: values.location,
        timezone: values.timezone,
        additionalInfo: values.additionalInfo,
      });
    }
  };

  return (
    <div className="space-y-6">
      <Button
        variant="ghost"
        onClick={() => navigate(paths.request)}
        className="mb-4"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        {t("partnerFinder.form.backToMyRequest", {
          defaultValue: "Back to My Request",
        })}
      </Button>

      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle>
            {isEditMode
              ? t("partnerFinder.form.editRequestTitle", {
                  defaultValue: "Edit Request",
                })
              : t("partnerFinder.form.createPartnerRequestTitle", {
                  defaultValue: "Create Partner Request",
                })}
          </CardTitle>
          <CardDescription>
            {isEditMode
              ? t("partnerFinder.form.editRequestDescription", {
                  defaultValue: "Update your partner request details",
                })
              : t("partnerFinder.form.createRequestDescription", {
                  defaultValue:
                    "Share your learning goals and preferences to help others find you",
                })}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="space-y-5 sm:space-y-6"
            >
              {/* German Level */}
              <FormField
                control={form.control}
                name="level"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("partnerFinder.form.levelLabel", {
                        defaultValue: "German Level *",
                      })}
                    </FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        value={field.value}
                        className="flex flex-wrap gap-4"
                      >
                        {LEVELS.map((level) => (
                          <div
                            key={level}
                            className="flex items-center space-x-2"
                          >
                            <RadioGroupItem value={level} id={level} />
                            <Label
                              htmlFor={level}
                              className="cursor-pointer font-normal"
                            >
                              {level}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormDescription>
                      {t("partnerFinder.form.levelDescription", {
                        defaultValue:
                          "Select your current German proficiency level",
                      })}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Meeting Preference */}
              <FormField
                control={form.control}
                name="meetingPreference"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("partnerFinder.form.meetingPreferenceRequiredLabel", {
                        defaultValue: "Meeting Preference *",
                      })}
                    </FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        value={field.value}
                        className="flex flex-wrap gap-4 sm:gap-6"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="online" id="online" />
                          <Label
                            htmlFor="online"
                            className="cursor-pointer font-normal"
                          >
                            {t("partnerFinder.values.meeting.online", {
                              defaultValue: "Online",
                            })}
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="in-person" id="in-person" />
                          <Label
                            htmlFor="in-person"
                            className="cursor-pointer font-normal"
                          >
                            {t("partnerFinder.values.meeting.in-person", {
                              defaultValue: "In-person",
                            })}
                          </Label>
                        </div>
                      </RadioGroup>
                    </FormControl>
                    <FormDescription>
                      {t("partnerFinder.form.meetingPreferenceDescription", {
                        defaultValue:
                          "How would you prefer to meet with your partner?",
                      })}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Learning Goal */}
              <FormField
                control={form.control}
                name="goal"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("partnerFinder.form.goalLabel", {
                        defaultValue: "Learning Goal *",
                      })}
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={t("partnerFinder.form.selectGoal", {
                              defaultValue: "Select a learning goal",
                            })}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {learningGoals.map((goal) => (
                          <SelectItem key={goal.id} value={goal.label}>
                            {translatePartnerGoal(t, goal.label)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      {t("partnerFinder.form.goalDescription", {
                        defaultValue:
                          "What is your primary goal for learning German?",
                      })}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Availability */}
              <FormField
                control={form.control}
                name="availability"
                render={() => (
                  <FormItem>
                    <div>
                      <FormLabel>
                        {t("partnerFinder.form.daysLabel", {
                          defaultValue: "Suggested Days *",
                        })}
                      </FormLabel>
                      <FormDescription className="mb-3">
                        {t("partnerFinder.form.daysDescription", {
                          defaultValue:
                            "Select the days you're typically available to meet",
                        })}
                      </FormDescription>
                    </div>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                      {DAYS.map((day) => (
                        <FormField
                          key={day}
                          control={form.control}
                          name="availability"
                          render={({ field }) => {
                            const handleDayChange = createDayChangeHandler(
                              field,
                              day,
                            );
                            return (
                              <FormItem
                                key={day}
                                className="flex flex-row items-start space-x-3 space-y-0"
                              >
                                <FormControl>
                                  <Checkbox
                                    checked={field.value?.includes(day)}
                                    onCheckedChange={handleDayChange}
                                  />
                                </FormControl>
                                <FormLabel className="text-sm font-normal cursor-pointer">
                                  {translatePartnerDay(t, day)}
                                </FormLabel>
                              </FormItem>
                            );
                          }}
                        />
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Location (if in-person) */}
              {meetingPreference === "in-person" && (
                <FormField
                  control={form.control}
                  name="location"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("partnerFinder.form.locationRequiredLabel", {
                          defaultValue: "Location *",
                        })}
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue
                              placeholder={t(
                                "partnerFinder.form.selectLocation",
                                { defaultValue: "Select a location" },
                              )}
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {locations.map((loc) => (
                            <SelectItem key={loc.id} value={loc.label}>
                              {translatePartnerLocation(t, loc.label)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        {t("partnerFinder.form.locationDescription", {
                          defaultValue:
                            "Where would you like to meet in person?",
                        })}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* Additional Information */}
              <FormField
                control={form.control}
                name="additionalInfo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("partnerFinder.form.additionalInfoRequiredLabel", {
                        defaultValue: "Additional Information *",
                      })}
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={t(
                          "partnerFinder.form.additionalInfoShortPlaceholder",
                          {
                            defaultValue:
                              "Tell potential partners about yourself, your goals, and what you're looking for...",
                          },
                        )}
                        className="min-h-[120px]"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      {t("partnerFinder.form.additionalInfoDescription", {
                        defaultValue:
                          "Provide details about yourself and what you're looking for in a partner",
                      })}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Submit Button */}
              <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto h-11 sm:h-10 order-1 sm:order-2"
                >
                  {getSubmitButtonText()}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(paths.request)}
                  disabled={isSubmitting}
                  className="w-full sm:w-auto h-11 sm:h-10 order-2 sm:order-1"
                >
                  {t("common.cancel", { defaultValue: "Cancel" })}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
